/**
 * Security Health Hook using React Query
 * 
 * Handles security health data fetching with React Query
 */

import { useQuery } from '@tanstack/react-query';
import { 
  getSecurityHealth,
} from '@/services/security-scanner';

/**
 * Query key factory for security health
 */
export const securityHealthKeys = {
  all: ['securityHealth'] as const,
  detail: (orgId: string | number, userId: string | number) => 
    [...securityHealthKeys.all, 'detail', orgId, userId] as const,
};

/**
 * Hook to fetch security health data
 * 
 * @example
 * const { data, isLoading, error } = useSecurityHealth({
 *   org_id: 19,
 *   user_id: 14
 * });
 * // data.security_health - SecurityHealth object with metrics
 * // data.org_id - Organization ID
 * // data.user_id - User ID
 */
export function useSecurityHealth(params?: {
  org_id?: number | string;
  user_id?: number | string;
}) {
  return useQuery({
    queryKey: securityHealthKeys.detail(
      params?.org_id || '',
      params?.user_id || ''
    ),
    queryFn: () => getSecurityHealth({
      org_id: params!.org_id!,
      user_id: params!.user_id!,
    }),
    enabled: !!params?.org_id && !!params?.user_id, // Only fetch if both IDs are provided
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

